import { linspace, Matrix, norm } from "../matrix/index.js";
import { euclidean, euclidean_squared } from "../metrics/index.js";
import { neumair_sum } from "../numerical/index.js";
import { DR } from "./DR.js";

/** @import {InputType} from "../index.js" */
/** @import {Metric} from "../metrics/index.js" */
/** @import {ParametersSQDMDS} from "./index.js" */

/**
 * SQuadMDS (Stochastic Quartet MDS)
 *
 * A lean Stochastic Quartet MDS improving global structure preservation in
 * neighbor embedding like t-SNE and UMAP.
 *
 * @class
 * @template {InputType} T
 * @extends DR<T, ParametersSQDMDS>
 * @category Dimensionality Reduction
 */
export class SQDMDS extends DR {
    /**
     * SQuadMDS: a lean Stochastic Quartet MDS improving global structure preservation in neighbor embedding like t-SNE
     * and UMAP.
     *
     * @param {T} X
     * @param {Partial<ParametersSQDMDS>} [parameters]
     * @see {@link https://arxiv.org/pdf/2202.12087.pdf}
     */
    constructor(X, parameters) {
        super(
            X,
            {
                d: 2,
                metric: euclidean,
                seed: 1212,
                decay_start: 0.1,
                decay_cte: 0.34, // 0.34
            },
            parameters,
        );

        this.init();
        if (this.parameter("metric") === "precomputed" && this.X.shape[0] !== this.X.shape[1]) {
            throw new Error("SQDMDS input data must be a square Matrix");
        }
    }

    init() {
        const N = this._N;
        const d = /** @type {number} */ (this.parameter("d"));

        // initialize helpers.
        this._add = this.__add(d);
        this._sub_div = this.__sub_div(d);
        this._minus = this.__minus(d);
        this._mult = this.__mult(d);
        this._LR_init = Math.max(2, 0.005 * N);
        this._LR = this._LR_init;
        const decay_cte = /** @type {number} */ (this.parameter("decay_cte"));
        this._offset = -Math.exp(-1 / decay_cte);
        this._momentums = new Matrix(N, d, 0);
        this._grads = new Matrix(N, d, 0);
        this._indices = linspace(0, N - 1);
        // initialize projection.
        const R = this._randomizer;
        this.Y = new Matrix(N, d, () => R.random - 0.5);

        // preparing metric for optimization.
        const this_metric = /** @type {Metric | "precomputed"} */ (this.parameter("metric"));
        if (this_metric === "precomputed") {
            /** @type {(i: number, j: number, X: Matrix) => number} */
            this._HD_metric = (i, j, X) => X.entry(i, j);
            /** @type {(i: number, j: number, X: Matrix) => number} */
            this._HD_metric_exaggeration = (i, j, X) => X.entry(i, j) ** 2;
        } else {
            this._HD_metric = (i, j, X) => this_metric(X.row(i), X.row(j));
            if (this_metric === euclidean) {
                this._HD_metric_exaggeration = (i, j, X) => euclidean_squared(X.row(i), X.row(j));
            } else {
                this._HD_metric_exaggeration = (i, j, X) => this_metric(X.row(i), X.row(j)) ** 2;
            }
        }
        return;
    }

    /**
     * Computes the projection.
     *
     * @param {number} [iterations=500] - Number of iterations. Default is `500`
     * @returns {T} The projection.
     */
    transform(iterations = 500) {
        this.check_init();
        const decay_start = /** @type {number} */ (this.parameter("decay_start"));
        this._decay_start = Math.round(decay_start * iterations);
        for (let i = 0; i < iterations; ++i) {
            this._step(i, iterations);
        }
        return this.projection;
    }

    /**
     * Computes the projection.
     *
     * @param {number} [iterations=500] - Number of iterations. Default is `500`
     * @returns {Generator<T, T, void>} The intermediate steps of the projection.
     */
    *generator(iterations = 500) {
        this.check_init();
        const decay_start = /** @type {number} */ (this.parameter("decay_start"));
        this._decay_start = Math.round(decay_start * iterations);
        for (let i = 0; i < iterations; ++i) {
            this._step(i, iterations);
            yield this.projection;
        }

        return this.projection;
    }

    /**
     * Performs an optimization step.
     *
     * @private
     * @param {number} i - Acutal iteration.
     * @param {number} iterations - Number of iterations.
     */
    _step(i, iterations) {
        if (this._LR_init === undefined || this._offset === undefined) throw new Error("Call init() first!");

        const decay_start = /** @type {number} */ (this.parameter("decay_start"));
        if (i > decay_start) {
            const decay_cte = /** @type {number} */ (this.parameter("decay_cte"));
            const offset = this._offset;
            const ratio = (i - decay_start) / (iterations - decay_start);
            this._LR = this._LR_init * (Math.exp(-(ratio * ratio) / decay_cte) + offset);
            this._distance_exaggeration = false;
        } else {
            this._distance_exaggeration = true;
        }
        this._nestrov_iteration(this._distance_exaggeration);
    }

    /**
     * Creates quartets of non overlapping indices.
     *
     * @private
     * @returns {Uint32Array[]}
     */
    __quartets() {
        if (!this._indices) throw new Error("Call init() first!");
        if (this._offset === undefined) throw new Error("Call init() first!");
        const N = this._N;
        const max_N = N - (N % 4);
        const R = this._randomizer;
        const shuffled_indices = R.choice(this._indices, max_N);
        const result = [];
        for (let i = 0; i < max_N; i += 4) {
            result.push(
                Uint32Array.of(
                    shuffled_indices[i],
                    shuffled_indices[i + 1],
                    shuffled_indices[i + 2],
                    shuffled_indices[i + 3],
                ),
            );
        }
        return result;
    }

    /**
     * Computes and applies gradients, and updates momentum.
     *
     * @private
     * @param {boolean} distance_exaggeration
     */
    _nestrov_iteration(distance_exaggeration) {
        if (!this._momentums || !this._grads || this._LR === undefined) throw new Error("Call init() first!");
        const momentums = this._momentums.mult(0.99, { inline: true });
        const LR = this._LR;
        const grads = this._fill_MDS_grads(this.Y.add(momentums), this._grads, distance_exaggeration);
        const [n, d] = momentums.shape;
        for (let i = 0; i < n; ++i) {
            const g_i = grads.row(i);
            const g_i_norm = norm(g_i);
            if (g_i_norm === 0) continue;
            const mul = LR / g_i_norm;
            const m_i = momentums.row(i);
            for (let j = 0; j < d; ++j) {
                m_i[j] -= mul * g_i[j];
            }
        } // momentums -= (LR / norm) * grads
        this.Y.add(momentums, { inline: true });
    }

    /**
     * Computes the gradients.
     *
     * @param {Matrix} Y - The Projection.
     * @param {Matrix} grads - The gradients.
     * @param {boolean} [exaggeration=false] - Whether or not to use early exaggeration. Default is `false`
     * @param {boolean} [zero_grad=true] - Whether or not to reset the gradient in the beginning. Default is `true`
     * @returns {Matrix} The gradients.
     */
    _fill_MDS_grads(Y, grads, exaggeration = false, zero_grad = true) {
        if (!this._HD_metric || !this._HD_metric_exaggeration || !this._add) throw new Error("Call init() first!");
        if (zero_grad) {
            // compute new gradients
            grads.values.fill(0);
        }
        const add = this._add;
        const X = this.X;
        let HD_metric;
        if (exaggeration === true) {
            HD_metric = this._HD_metric_exaggeration;
        } else {
            HD_metric = this._HD_metric;
        }

        const D_quartet = new Float64Array(6);
        const quartets = this.__quartets();
        for (const [i, j, k, l] of quartets) {
            // compute quartet's HD distances.
            D_quartet[0] = HD_metric(i, j, X);
            D_quartet[1] = HD_metric(i, k, X);
            D_quartet[2] = HD_metric(i, l, X);
            D_quartet[3] = HD_metric(j, k, X);
            D_quartet[4] = HD_metric(j, l, X);
            D_quartet[5] = HD_metric(k, l, X);

            const D_quartet_sum = neumair_sum(D_quartet);

            if (D_quartet_sum > 0) {
                for (let i = 0; i < 6; ++i) {
                    D_quartet[i] /= D_quartet_sum;
                    D_quartet[i] += 1e-11;
                }
            }
            const [gi, gj, gk, gl] = this._compute_quartet_grads(Y, [i, j, k, l], D_quartet);

            // add is inline, row acces the matrix
            add(grads.row(i), gi);
            add(grads.row(j), gj);
            add(grads.row(k), gk);
            add(grads.row(l), gl);
        }
        return grads;
    }

    /**
     * Quartet gradients for a projection.
     *
     * @private
     * @param {Matrix} Y - The acutal projection.
     * @param {number[]} quartet - The indices of the quartet.
     * @param {Float64Array} D_hd - The high-dimensional distances of the quartet.
     * @returns {Float64Array[]} The gradients for the quartet.
     */
    _compute_quartet_grads(Y, quartet, [p_ab, p_ac, p_ad, p_bc, p_bd, p_cd]) {
        const [a, b, c, d] = quartet.map((index) => Y.row(index));
        // LD distances, add a small number just in case
        const d_ab = euclidean(a, b) + 1e-12;
        const d_ac = euclidean(a, c) + 1e-12;
        const d_ad = euclidean(a, d) + 1e-12;
        const d_bc = euclidean(b, c) + 1e-12;
        const d_bd = euclidean(b, d) + 1e-12;
        const d_cd = euclidean(c, d) + 1e-12;
        const sum_LD_dist = neumair_sum([d_ab, d_ac, d_ad, d_bc, d_bd, d_cd]);

        // for each element of the sum: use the same gradient function and just permute the points given in input.
        const [gA1, gB1, gC1, gD1] = this._ABCD_grads(
            a,
            b,
            c,
            d,
            d_ab,
            d_ac,
            d_ad,
            d_bc,
            d_bd,
            d_cd,
            p_ab,
            sum_LD_dist,
        );
        const [gA2, gC2, gB2, gD2] = this._ABCD_grads(
            a,
            c,
            b,
            d,
            d_ac,
            d_ab,
            d_ad,
            d_bc,
            d_cd,
            d_bd,
            p_ac,
            sum_LD_dist,
        );
        const [gA3, gD3, gC3, gB3] = this._ABCD_grads(
            a,
            d,
            c,
            b,
            d_ad,
            d_ac,
            d_ab,
            d_cd,
            d_bd,
            d_bc,
            p_ad,
            sum_LD_dist,
        );
        const [gB4, gC4, gA4, gD4] = this._ABCD_grads(
            b,
            c,
            a,
            d,
            d_bc,
            d_ab,
            d_bd,
            d_ac,
            d_cd,
            d_ad,
            p_bc,
            sum_LD_dist,
        );
        const [gB5, gD5, gA5, gC5] = this._ABCD_grads(
            b,
            d,
            a,
            c,
            d_bd,
            d_ab,
            d_bc,
            d_ad,
            d_cd,
            d_ac,
            p_bd,
            sum_LD_dist,
        );
        const [gC6, gD6, gA6, gB6] = this._ABCD_grads(
            c,
            d,
            a,
            b,
            d_cd,
            d_ac,
            d_bc,
            d_ad,
            d_bd,
            d_ab,
            p_cd,
            sum_LD_dist,
        );

        if (!this._add) throw new Error("Call init() first!");
        const add = this._add;
        const gA = add(gA1, gA2, gA3, gA4, gA5, gA6);
        const gB = add(gB1, gB2, gB3, gB4, gB5, gB6);
        const gC = add(gC1, gC2, gC3, gC4, gC5, gC6);
        const gD = add(gD1, gD2, gD3, gD4, gD5, gD6);

        return [gA, gB, gC, gD];
    }

    /**
     * Gradients for one element of the loss function's sum.
     *
     * @private
     * @param {Float64Array} a
     * @param {Float64Array} b
     * @param {Float64Array} c
     * @param {Float64Array} d
     * @param {number} d_ab
     * @param {number} d_ac
     * @param {number} d_ad
     * @param {number} d_bc
     * @param {number} d_bd
     * @param {number} d_cd
     * @param {number} p_ab
     * @param {number} sum_LD_dist
     * @returns {Float64Array[]}
     */
    _ABCD_grads(a, b, c, d, d_ab, d_ac, d_ad, d_bc, d_bd, d_cd, p_ab, sum_LD_dist) {
        if (!this._minus || !this._add || !this._mult || !this._sub_div) throw new Error("Call init() first!");
        const ratio = d_ab / sum_LD_dist;
        const twice_ratio = 2 * ((p_ab - ratio) / sum_LD_dist);
        const minus = this._minus;
        const add = this._add;
        const mult = this._mult;
        const sub_div = this._sub_div;
        // no side effects because sub_div creates new arrays, and the inline functions work on this new created arrays.
        const gA = mult(
            minus(mult(add(sub_div(a, b, d_ab), sub_div(a, c, d_ac), sub_div(a, d, d_ad)), ratio), sub_div(a, b, d_ab)),
            twice_ratio,
        );
        const gB = mult(
            minus(mult(add(sub_div(b, a, d_ab), sub_div(b, c, d_bc), sub_div(b, d, d_bd)), ratio), sub_div(b, a, d_ab)),
            twice_ratio,
        );
        const gC = mult(add(sub_div(c, a, d_ac), sub_div(c, b, d_bc), sub_div(c, d, d_cd)), ratio * twice_ratio);
        const gD = mult(add(sub_div(d, a, d_ad), sub_div(d, b, d_bd), sub_div(d, c, d_cd)), ratio * twice_ratio);
        return [gA, gB, gC, gD];
    }

    /**
     * Inline!
     *
     * @param {number} d
     */
    __minus(d) {
        return /** @type {(a: Float64Array, b: Float64Array) => Float64Array} */ (a, b) => {
            for (let i = 0; i < d; ++i) {
                a[i] -= b[i];
            }
            return a;
        };
    }

    /**
     * Inline!
     *
     * @param {number} d
     */
    __add(d) {
        return /** @type {(...summands: Float64Array[]) => Float64Array} */ (...summands) => {
            const n = summands.length;
            const s1 = summands[0];
            for (let j = 1; j < n; ++j) {
                const summand = summands[j];
                for (let i = 0; i < d; ++i) {
                    s1[i] += summand[i];
                }
            }
            return s1;
        };
    }

    /**
     * Inline!
     *
     * @param {number} d
     */
    __mult(d) {
        return /** @type {(a: Float64Array, v: number) => Float64Array} */ (a, v) => {
            for (let i = 0; i < d; ++i) {
                a[i] *= v;
            }
            return a;
        };
    }

    /**
     * Creates a new array `(x - y) / div`.
     *
     * @param {number} d
     */
    __sub_div(d) {
        return /** @type {(x: Float64Array, y: Float64Array, div: number) => Float64Array} */ (x, y, div) => {
            return Float64Array.from({ length: d }, (_, i) => (x[i] - y[i]) / div);
        };
    }

    /**
     * @template {InputType} T
     * @param {T} X
     * @param {Partial<ParametersSQDMDS>} [parameters]
     * @returns {T}
     */
    static transform(X, parameters) {
        const dr = new SQDMDS(X, parameters);
        return dr.transform();
    }

    /**
     * @template {InputType} T
     * @param {T} X
     * @param {Partial<ParametersSQDMDS>} [parameters]
     * @returns {Generator<T, T, void>}
     */
    static *generator(X, parameters) {
        const dr = new SQDMDS(X, parameters);
        yield* dr.generator();
        return dr.projection;
    }

    /**
     * @template {InputType} T
     * @param {T} X
     * @param {Partial<ParametersSQDMDS>} [parameters]
     * @returns {Promise<T>}
     */
    static async transform_async(X, parameters) {
        const dr = new SQDMDS(X, parameters);
        return dr.transform_async();
    }
}
